// --- Constants ---
const PRODUCT_IDS = {
  DANSANI_OPPSTARTTILLEGG: 43793094508743,
  ROROSHETTA_KOBLINGSBOKS: 44882801262791,
  ROROSHETTA_FILTER: 43259463368903
};

// --- Cart Data Cache ---
const cartCache = {
  data: null,
  timestamp: 0,
  ttl: 1000 // ms
};

async function getCachedCartData() {
  const now = Date.now();
  if (!cartCache.data || (now - cartCache.timestamp) > cartCache.ttl) {
    cartCache.data = await fetch('/cart.js').then(r => r.json());
    cartCache.timestamp = now;
  }
  return cartCache.data;
}

// --- Validation ---
function validateCartItem(item) {
  return item &&
    typeof item.vendor === 'string' &&
    typeof item.title === 'string' &&
    Array.isArray(item.options_with_values);
}

// --- Product Fetch Utility ---
async function fetchProductSafely(handle) {
  try {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    console.warn(`Failed to fetch product ${handle}:`, error);
    return null;
  }
}

// --- Product Data Sanitization ---
function sanitizeProductData(product) {
  if (!product || typeof product !== 'object') return null;
  return {
    handle: String(product.handle || ''),
    title: String(product.title || ''),
    price: Number(product.price) || 0
  };
}

// --- Business Logic Classes ---
class ProductRuleEngine {
  static checkDansaniRules(items) {
    let haveCalidris = false;
    for (const item of items) {
      if (!validateCartItem(item)) continue;
      const brandName = item.vendor.toLowerCase();
      if (brandName === 'dansani') {
        const isCalidris = item.title.toLowerCase().includes('calidris');
        for (const option of item.options_with_values) {
          const optionName = option.name.toLowerCase();
          const optionValue = option.value.toLowerCase();
          if (optionName === 'farge') {
            if ((isCalidris && optionValue !== 'hvit matt') ||
                (!isCalidris && optionValue === 'ncs farge')) {
              haveCalidris = true;
              break;
            }
          }
        }
      }
      if (haveCalidris) break;
    }
    return haveCalidris;
  }

  static checkRoroshettaRules(items) {
    let haveRoroshetta = false;
    let roroshettaProductHandle = '';
    for (const item of items) {
      if (!validateCartItem(item)) continue;
      const brandName = item.vendor.toLowerCase();
      if (brandName === 'røroshetta') {
        for (const option of item.options_with_values) {
          const optionName = option.name.toLowerCase();
          const optionValue = option.value.toLowerCase();
          if (optionName === 'ventilasjonstype') {
            if (optionValue === 'ekstern motor' || optionValue === 'sentralventilasjon') {
              haveRoroshetta = true;
              roroshettaProductHandle = 'roroshetta-koblingsboks-ved-ekstern-ac-motor';
            } else if (optionValue === 'resirkulasjon') {
              haveRoroshetta = true;
              roroshettaProductHandle = 'monoblockfilter-sense-karbonfilter';
            }
            break;
          }
        }
      }
      if (haveRoroshetta) break;
    }
    return { haveRoroshetta, roroshettaProductHandle };
  }
}

class CartUpdater {
  static async updateCart(updates) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Cart update failed');
      const json = await response.json();
      // Invalidate cache to ensure fresh reads post-update
      cartCache.data = null;
      cartCache.timestamp = 0;
      return json;
    } catch (error) {
      console.error('Cart update error:', error);
      return null;
    }
  }
}

// --- Main CartDrawer Component ---
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isIosDevice = typeof window !== 'undefined' &&
        window.navigator &&
        window.navigator.platform &&
        (/iP(ad|hone|od)/.test(window.navigator.platform) ||
          (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1));
      this.htmlElement = document.documentElement;
      this.drawerInner = this.querySelector('.js-drawer-inner');
      this.drawerClose = this.querySelectorAll('.js-cartdrawer-close');
      this.errorMessage = this.querySelector('.js-drawer-error');
      this.totalCart = this.querySelector('.js-drawer-total');
      this.drawerTable = this.querySelector('.js-cart-drawer-table');
      this.drawerFooter = this.querySelector('.js-cartdrawer-footer');
      this.drawerNote = this.querySelector('.js-cart-note');
      this.cartItemsInform = this.querySelectorAll('.js-product-remove-inform');
      this.cartExtras = this.querySelector('.js-cart-extras');
      this.pageReload = false;
      this.cartCheckout = this.querySelector('button.cart-drawer__checkout-button');
      this.updateInProgress = false;
      this.bindEvents();
    }

    quantityUpdateUnsubscriber = undefined;
    cartUpdateUnsubscriber = undefined;

    connectedCallback() {
      requirejs(['pubsub'], () => {
        this.quantityUpdateUnsubscriber = subscribeEvent(
          PUB_SUB_EVENTS.quantityUpdate,
          ({ source, data }) => {
            this.renderContent(data?.lineId, null);
            this.open();
          }
        );

        this.cartUpdateUnsubscriber = subscribeEvent(
          PUB_SUB_EVENTS.cartUpdate,
          ({ source, data }) => {
            if (source == 'product-form') {
              this.renderContent(null, data?.sections?.['cart-drawer']);
              this.checkExtraItems(data?.sections?.['cart-drawer']);
              this.open();
            }
          }
        );
      })
    }

    disconnectedCallback() {
      if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    checkExtraItems(data) {
      if (!data) return;
      const dom = new DOMParser().parseFromString(String(data), 'text/html');
      const extras = dom.querySelector('.js-cart-extras');
      if(extras){
        this.querySelector('.js-cart-extras').className = extras.className;
      }
    }

    async updateExtrasInfo() {
      if (this.updateInProgress) return;
      this.updateInProgress = true;
      
      try {
        const cartData = await getCachedCartData();
        const items = cartData.items;
        let extraProduct = null;
        let shouldShowExtras = false;

        if (ProductRuleEngine.checkDansaniRules(items)) {
          extraProduct = await fetchProductSafely('dansani-oppstarttillegg-valgfri-farge');
          shouldShowExtras = !!extraProduct;
        } else {
          const { haveRoroshetta, roroshettaProductHandle } = ProductRuleEngine.checkRoroshettaRules(items);
          if (haveRoroshetta && roroshettaProductHandle) {
            extraProduct = await fetchProductSafely(roroshettaProductHandle);
            shouldShowExtras = !!extraProduct;
          }
        }

        if (this.cartExtras) {
          if (shouldShowExtras && extraProduct) {
            this.cartExtras.classList.add('active');
            
            const extraLink = this.cartExtras.querySelector('.cart-drawer__extras-link');
            const extraPriceEl = this.cartExtras.querySelector('.cart-drawer__extras-price');
            const extraProductText = this.cartExtras.querySelector('.cart-drawer__extras-text');
            
            if (extraLink) {
              extraLink.href = `/products/${extraProduct.handle}`;
              extraLink.textContent = extraProduct.title;
            }
            
            const priceInCents = extraProduct?.variants?.[0]?.price ?? 
                              extraProduct?.price_min ?? 
                              extraProduct?.price ?? 
                              null;
            
            if (extraPriceEl && priceInCents != null) {
              extraPriceEl.textContent = this.formatPrice(Number(priceInCents));
            }
            
            if (extraProductText && extraProduct?.vendor) {
              const vendor = extraProduct.vendor.toLowerCase();
              
              if (vendor === 'røroshetta') {
                extraProductText.textContent = 'Handlekurven blir automatisk oppdatert.';
              } else if (vendor === 'dansani') {
                const formattedPrice = priceInCents != null ? this.formatPrice(Number(priceInCents)) : '';
                extraProductText.innerHTML = `
                  Handlekurven blir automatisk oppdatert. Oppstarttillegg <span class="cart-drawer__extras-price">${formattedPrice}</span>
                  betales én gang per eik og valgfri farge uansett antall produkter.
                `;
              }
            }
          } else {
            this.cartExtras.classList.remove('active');
          }
        }
      } catch (error) {
        console.error('updateExtrasInfo error:', error);
      } finally {
        this.updateInProgress = false;
      }
    }

    formatPrice(price) {
      return (price / 100).toLocaleString('no-NO', { style: 'currency', currency: 'NOK' }).replace(',00','');
    }

    bindEvents() {
      window.onbeforeunload = () => {
        this.removeItemFromCart((this.pageReload = true));
      };

      this.drawerClose.forEach((element) => {
        element.addEventListener('click', () => {
          this.removeItemFromCart(this.pageReload);
        });
      });

      this.cartCheckout.addEventListener('click', async (e) => {
        e.preventDefault();

        // --- Update Cart Note ---
        const cartNote = document.querySelector('.js-cart-note textarea');
        if (cartNote) {
          const cartNoteValue = cartNote.value;
          await fetch(`/cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: cartNoteValue })
          }).catch(() => false);
        }

        const cartData = await getCachedCartData();
        const items = cartData.items;
        let updates = {};

        // === DANSANI (Calidris / Oppstarttillegg Valgfri Farge) ===
        const extraItemsInCart = items
          .filter((item) => item.id === PRODUCT_IDS.DANSANI_OPPSTARTTILLEGG)
          .reduce((acc, item) => acc + item.quantity, 0);

        const uniqueNonWhiteColors = new Set();
        const vendorDansaniItems = items.filter((item) => item.vendor.toLowerCase() === 'dansani');

        vendorDansaniItems.forEach((item) => {
          const isCalidris = item.title.toLowerCase().includes('calidris');
          item.options_with_values.forEach((option) => {
            const isFarge = option.name.toLowerCase() === 'farge';
            const value = option.value.toLowerCase();
            if (isFarge) {
              if ((isCalidris && value !== 'hvit matt') || (!isCalidris && value === 'ncs farge')) {
                uniqueNonWhiteColors.add(option.value.toLowerCase());
              }
            }
          });
        });

        const uniqueColorCount = uniqueNonWhiteColors.size;

        if (extraItemsInCart !== uniqueColorCount) {
          updates[PRODUCT_IDS.DANSANI_OPPSTARTTILLEGG] = uniqueColorCount; 
        }

        // === RØROSHetta (Koblingsboks / Filter) ===
        let koblingsboksCount = 0;
        let filterCount = 0;

        const vendorRoroshettaItems = items.filter(
          (item) => item.vendor.toLowerCase() === 'røroshetta'
        );

        vendorRoroshettaItems.forEach((item) => {
          const quantity = item.quantity;
          item.options_with_values.forEach((option) => {
            const isVentilasjonstype = option.name.toLowerCase() === 'ventilasjonstype';
            const value = option.value.toLowerCase();

            if (isVentilasjonstype) {
              if (value === 'ekstern motor' || value === 'sentralventilasjon') {
                koblingsboksCount += quantity; 
              } else if (value === 'resirkulasjon') {
                filterCount += quantity; 
              }
            }
          });
        });

        // control current cart items
        const currentKoblingsboks = items
          .filter((item) => item.id === PRODUCT_IDS.ROROSHETTA_KOBLINGSBOKS)
          .reduce((acc, item) => acc + item.quantity, 0);

        const currentFilter = items
          .filter((item) => item.id === PRODUCT_IDS.ROROSHETTA_FILTER)
          .reduce((acc, item) => acc + item.quantity, 0);

        if (currentKoblingsboks !== koblingsboksCount) {
          updates[PRODUCT_IDS.ROROSHETTA_KOBLINGSBOKS] = koblingsboksCount;
        }

        if (currentFilter !== filterCount) {
          updates[PRODUCT_IDS.ROROSHETTA_FILTER] = filterCount;
        }

        // === Send updates if any ===
        if (Object.keys(updates).length > 0) {
          const result = await CartUpdater.updateCart(updates);
          if (result) {
            window.location.href = '/checkout';
          } else {
            this.errorMessage.classList.remove('hidden');
            setTimeout(() => this.errorMessage.classList.add('hidden'), 3000);
          }
        } else {
          window.location.href = '/checkout';
        }
      });
    }

    removeItemFromCart(pageReload) {
      const variantKeys = JSON.parse(localStorage.getItem('removedItem'));
      this.close();

      if (typeof variantKeys !== 'undefined') {
        const data = {
          updates: {},
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        };
        variantKeys?.forEach((element) => {
          data.updates[element] = 0;
        });
        const body = JSON.stringify(data);

        fetch(`${window.routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body }
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.description) console.warn(response.description);
            this.cartItemsInform.forEach((item) => {
              item.hidden = true;
            });
            localStorage.removeItem('removedItem');
          })
          .catch((err) => {
            console.error(err);
          });
      }
    }

    async open() {
      this.classList.add('is-open');
      if (this.isIosDevice && !this.htmlElement.classList.contains('off-canvas-menu')) {
        this.htmlElement.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.left = '0';
        document.body.style.top = '0';
      }
      await this.updateExtrasInfo();
    }

    close() {
      this.classList.remove('is-open');
      this.htmlElement.classList.remove('off-canvas-drawer');
      if (this.isIosDevice && !this.htmlElement.classList.contains('off-canvas-menu')) {
        this.htmlElement.style.overflow = '';
        document.body.style.position = '';
        document.body.style.left = '';
        document.body.style.top = '';
      }
    }

    async renderContent(cartItemId, html) {
      if (!this.drawerTable) return;

      if (!cartItemId && html) {
        const totalPrice = this.getSectionDOM(html, '.js-cart-drawer-ajax').textContent;
        this.totalCart.innerHTML = localCurrency.formatPrice(totalPrice);
        this.totalCart.setAttribute('data-totalprice', totalPrice);
        this.renderTableProducts(html, cartItemId);
      }

      if (cartItemId && !html) {
        await fetch(`${window.routes.cart_url}?view=ajax`)
          .then((response) => response.text())
          .then((html) => {
            const totalPrice = this.getSectionDOM(html, '.js-cart-drawer-ajax').textContent;
            this.totalCart.innerHTML = localCurrency.formatPrice(totalPrice);
            this.renderTableProducts(html, cartItemId);
          })
          .catch((error) => {
            this.errorMessage.classList.remove('hidden');
            const clearErrorMessage = debounce((event) => {
              this.errorMessage.classList.add('hidden');
            }, 3000);
            clearErrorMessage();
          })
          .finally(() => {
            updateLazyLoad?.();
          });
      }
      await this.updateExtrasInfo();
    }

    getSectionDOM(html, selector = '.js-cart-drawer-ajax') {
      const dom = new DOMParser().parseFromString(html, 'text/html');
      if (selector) return dom.querySelector(selector);
      return dom;
    }

    renderTableProducts(html, cartItemId) {
      const fetchedDOM = this.getSectionDOM(html, null);
      const drawerHtml = fetchedDOM.querySelector('.js-cart-drawer-table');
      const drawerNote = fetchedDOM.querySelector('.js-cart-note');

      const isCartEmpty = drawerHtml.querySelector('.js-drawer-empty');
      const updateCartItem = drawerHtml.querySelector(`cart-item[data-id="${cartItemId}"]`);

      // Cart Empty
      if (isCartEmpty || !cartItemId) {
        this.drawerTable.innerHTML = drawerHtml.innerHTML;
        this.drawerNote?.remove();
        this.drawerNote = null;
      }

      // Update line item
      if (!updateCartItem && cartItemId) {
        const removedCartItem = this.querySelector(`cart-item[data-id="${cartItemId}"]`);
        removedCartItem && removedCartItem.remove();
      }

      // Update cart note
      if (drawerNote && !this.drawerNote) {
        this.drawerFooter.insertAdjacentHTML('afterbegin', drawerNote.outerHTML);
        this.drawerNote = this.querySelector('.js-cart-note');
      }

      if (updateLazyLoad) updateLazyLoad();
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}